import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import './App.css';
import AllRequests from './AllRequests';
import AddProvider from './AddProvider';

function MainApp() {
  const [step, setStep] = useState(1);
  const [questions, setQuestions] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [services, setServices] = useState([]);
  const [cities, setCities] = useState([]); // For service request form
  const [southAfricanCities, setSouthAfricanCities] = useState([]); // For provider registration
  const [timeFilter] = useState('1y');
  const [initialData, setInitialData] = useState(null);
  const [isProviderMode, setIsProviderMode] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm();
  const { register: registerProvider, handleSubmit: handleProviderSubmit, formState: { errors: errorsProvider } } = useForm();

  const fetchRecentRequests = useCallback(async () => {
    try {
      const res = await axios.get(`/api/requests?time=${timeFilter}`);
      console.log('Fetched recent requests:', res.data);
      setRecentRequests(res.data.slice(0, 8));
    } catch (error) {
      console.error('Error fetching recent requests:', error.response ? error.response.data : error.message);
    }
  }, [timeFilter]);

  const fetchServices = useCallback(async () => {
    try {
      const res = await axios.get('/api/services');
      console.log('Fetched services:', res.data.services);
      setServices(res.data.services || []);
    } catch (error) {
      console.error('Error fetching services:', error.response ? error.response.data : error.message);
    }
  }, []);

  const fetchCities = useCallback(async () => {
    try {
      const res = await axios.get('/api/cities');
      console.log('Fetched cities:', res.data.cities);
      setCities(res.data.cities || []);
    } catch (error) {
      console.error('Error fetching cities:', error.response ? error.response.data : error.message);
    }
  }, []);

  const fetchSouthAfricanCities = useCallback(async () => {
    try {
      const res = await axios.get('/api/south-african-cities');
      console.log('Fetched South African cities:', res.data.cities);
      setSouthAfricanCities(res.data.cities || []);
    } catch (error) {
      console.error('Error fetching South African cities:', error.response ? error.response.data : error.message);
    }
  }, []);

  useEffect(() => {
    fetchRecentRequests();
    fetchServices();
    fetchCities();
    fetchSouthAfricanCities(); // Add this
  }, [fetchRecentRequests, fetchServices, fetchCities, fetchSouthAfricanCities]);

  const fetchQuestions = async (data) => {
    try {
      const serviceToSend = data.service === 'add-new' && data.newService ? data.newService : data.service;
      console.log('Sending to Gemini API:', { serviceCategory: serviceToSend, city: data.city, contactDetails: { email: data.email, phone: data.phone } });
      const res = await axios.post('/api/requests/questions', {
        serviceCategory: serviceToSend,
        city: data.city,
        contactDetails: { email: data.email, phone: data.phone }
      });
      console.log('Questions response:', res.data);
      setQuestions(res.data.questions || []);
      setInitialData({ ...data, service: serviceToSend });
      setStep(2);
    } catch (error) {
      console.error('Error fetching questions:', error.response ? error.response.data : error.message);
    }
  };

  const onInitialSubmit = (data) => {
    console.log('Initial submit data:', data);
    fetchQuestions(data);
  };

  const onFinalSubmit = async (data) => {
    const answers = questions.map((_, index) => data[`answer${index}`] || '');
    const payload = {
      serviceCategory: initialData.service,
      city: initialData.city,
      contactDetails: {
        email: initialData.email,
        phone: initialData.phone
      },
      answers: answers
    };
    console.log('Final submit payload:', payload);
    try {
      const res = await axios.post('/api/requests', payload);
      console.log('Submission response:', res.data);
      reset();
      setQuestions([]);
      setInitialData(null);
      setStep(1);
      fetchRecentRequests();
    } catch (error) {
      console.error('Error submitting request:', error.response ? error.response.data : error.message);
    }
  };

  const onProviderSubmit = async (data) => {
    let selectedServices = Array.isArray(data.existingServices) ? data.existingServices : (data.existingServices ? [data.existingServices] : []);
    if (data.newServices) {
      const newServicesArray = data.newServices.split(',').map(s => s.trim()).filter(s => s);
      selectedServices = [...selectedServices, ...newServicesArray];
    }
    const providerData = {
      name: data.name,
      email: data.email,
      services: selectedServices,
      cities: data.existingCities // Use selected cities from dropdown
    };
    try {
      const res = await axios.post('/api/providers/register', providerData);
      console.log('Provider registration response:', res.data);
      setRegistrationSuccess(true);
      fetchServices();
      fetchCities();
    } catch (error) {
      console.error('Error submitting provider registration:', error.response ? error.response.data : error.message);
    }
  };

  const toggleProviderMode = () => {
    setIsProviderMode(!isProviderMode);
    setRegistrationSuccess(false);
    setStep(1);
  };

  const serviceValue = watch('service');

  if (isProviderMode) {
    return (
      <div className="container-fluid mt-5">
        <Helmet>
          <title>Fixxzap - Register as a Service Provider</title>
          <meta name="description" content="Join Fixxzap as a service provider. Register to offer plumbing, electrical, and other local services." />
          <meta name="keywords" content="service provider registration, local services, Fixxzap" />
          <meta property="og:title" content="Fixxzap - Register as a Service Provider" />
          <meta property="og:description" content="Join Fixxzap as a service provider. Register to offer plumbing, electrical, and other local services." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://fixxzap.com" />
          <meta property="og:image" content="https://fixxzap.com/logo.jpg" />
          <link rel="canonical" href="https://fixxzap.com" />
        </Helmet>
        {registrationSuccess ? (
          <div className="alert alert-success text-center">
            <h4>Registration Submitted</h4>
            <p>Your provider registration is pending approval. Please check your email ({watch('email') || 'your email'}) for verification instructions.</p>
            <button className="btn btn-primary" onClick={toggleProviderMode}>
              Back to Service Requests
            </button>
          </div>
        ) : (
          <div className="card p-4 shadow-sm mx-auto" style={{ maxWidth: '800px' }}>
            <h2 className="text-center mb-4">Register as a Service Provider</h2>
            <p className="text-center text-muted mb-4">After submission, you’ll need to provide verification documents for approval.</p>
            <form onSubmit={handleProviderSubmit(onProviderSubmit)}>
              <div className="mb-3">
                <label className="form-label">Business Name</label>
                <input className="form-control" {...registerProvider("name", { required: 'Business name is required' })} />
                {errorsProvider.name && <span className="text-danger">{errorsProvider.name.message}</span>}
              </div>
              <div className="mb-3">
                <label className="form-label">Email</label>
                <input className="form-control" type="email" {...registerProvider("email", { required: 'Email is required' })} />
                {errorsProvider.email && <span className="text-danger">{errorsProvider.email.message}</span>}
              </div>
              <div className="mb-3">
                <label className="form-label">Services (select multiple or add new)</label>
                <select
                  multiple
                  {...registerProvider("existingServices")}
                  className="form-control mb-2"
                >
                  {services.map((service, index) => (
                    <option key={index} value={service}>{service}</option>
                  ))}
                </select>
                <input
                  className="form-control"
                  {...registerProvider("newServices")}
                  placeholder="Add new services (comma-separated, e.g., Painting, Roofing)"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Cities (select multiple)</label>
                <select
                  multiple
                  {...registerProvider("existingCities", { required: 'At least one city is required' })}
                  className="form-control mb-2"
                  style={{ height: '100px' }}
                >
                  {southAfricanCities.map((city, index) => (
                    <option key={index} value={city}>{city}</option>
                  ))}
                </select>
                {errorsProvider.existingCities && <span className="text-danger">{errorsProvider.existingCities.message}</span>}
                <input
                  className="form-control"
                  {...registerProvider("newCities")}
                  placeholder="Add new cities (comma-separated, e.g., Pretoria, Bloemfontein)"
                />
              </div>
              <button type="submit" className="btn btn-primary w-100">Submit Registration</button>
              <button type="button" className="btn btn-link w-100 mt-2" onClick={toggleProviderMode}>
                Back to Service Requests
              </button>
            </form>
          </div>
        )}
      </div>
    );
  }

  // ... (rest of MainApp remains unchanged: service request form, recent requests, etc.) ...

  return (
    <div className="container-fluid mt-5">
      <Helmet>
        <title>Fixxzap - Local Service Requests</title>
        <meta name="description" content="Connect with local service providers for plumbing, electrical, and more on Fixxzap. Submit requests and find help fast." />
        <meta name="keywords" content="service requests, local providers, plumbing, electrical, Fixxzap" />
        <meta property="og:title" content="Fixxzap - Local Service Requests" />
        <meta property="og:description" content="Connect with local service providers for plumbing, electrical, and more on Fixxzap." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fixxzap.com" />
        <meta property="og:image" content="https://fixxzap.com/logo.jpg" />
        <link rel="canonical" href="https://fixxzap.com" />
      </Helmet>
      <div className="row">
        <div className="col-md-4 mx-auto">
          <h1 className="text-center mb-4 fixxzap-title">Fixxzap</h1>
          {step === 1 ? (
            <form onSubmit={handleSubmit(onInitialSubmit)} className="card p-4 shadow-sm mb-4">
              <div className="mb-3">
                <label className="form-label">Service</label>
                <select
                  {...register('service', { required: 'Service is required' })}
                  className="form-control mb-2"
                  defaultValue=""
                >
                  <option value="">Select a service</option>
                  {services.map((service, index) => (
                    <option key={index} value={service}>{service}</option>
                  ))}
                  <option value="add-new">Add new service</option>
                </select>
                {errors.service && <span className="text-danger">{errors.service.message}</span>}
                {serviceValue === 'add-new' && (
                  <div className="mt-2">
                    <input
                      {...register('newService', { 
                        required: 'New service name is required', 
                        pattern: { 
                          value: /^(?!.*(\b(?:drug|illegal|illicit|weapon|escort|sex|porn|prostitution|violence|hate|terror|exploit|abuse|scam|fraud|hack|malware|nudity|explicit|racist|bigot|extremist|underage|child|exploitation|trafficking|murder|assassin|gambling|betting|forgery|counterfeit|smuggling|bootleg|blackmarket|darkweb|cybercrime|harassment|bullying|stalking|revenge|rape|incest|bestiality|pedophil|zoo|snuff|gore|bloodshed|torture|cannibal|suicide|selfharm|anarchist|radical|fundamentalist|separatist|insurgent|paramilitary|mafia|cartel|gang|cult|sectarian|genocide|warcrime|holocaust|ethnic\s?cleansing|fascist|nazi|supremacist|misogynist|transphobic|homophobic|xenophobic|islamophobic|antisemitic|anti\s?semitic|anti\s?jewish|anti\s?muslim|anti\s?christian|anti\s?hindu|anti\s?sikh|anti\s?buddhist|anti\s?atheist|anti\s?agnostic)\b))/i,
                          message: 'No illegal or inappropriate services allowed' 
                        },
                        maxLength: { value: 50, message: 'Service name too long' }
                      })}
                      className="form-control"
                      placeholder="Type new service (e.g., Carpentry)"
                    />
                    {errors.newService && <span className="text-danger">{errors.newService.message}</span>}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <input {...register('city', { required: 'City is required' })} className="form-control" placeholder="City (e.g., Cape Town)" />
                {errors.city && <span className="text-danger">{errors.city.message}</span>}
              </div>
              <div className="mb-3">
                <input {...register('email', { required: 'Email is required' })} className="form-control" placeholder="Your email" type="email" />
                {errors.email && <span className="text-danger">{errors.email.message}</span>}
              </div>
              <div className="mb-3">
                <input {...register('phone', { required: 'Phone is required' })} className="form-control" placeholder="Your phone" type="tel" />
                {errors.phone && <span className="text-danger">{errors.phone.message}</span>}
              </div>
              <button type="submit" className="btn btn-primary w-100">Next</button>
            </form>
          ) : (
            <form onSubmit={handleSubmit(onFinalSubmit)} className="card p-4 shadow-sm mb-4">
              <h3 className="mb-3">Please Answer the Following Questions</h3>
              {questions.length > 0 ? (
                questions.map((q, index) => (
                  <div className="mb-3" key={index}>
                    <label className="form-label">{q.text}</label>
                    {q.type === 'select' ? (
                      <select {...register(`answer${index}`, { required: true })} className="form-control">
                        <option value="">Select an option</option>
                        {q.options.map((option, i) => (
                          <option key={i} value={option}>{option}</option>
                        ))}
                      </select>
                    ) : (
                      <input {...register(`answer${index}`, { required: true })} className="form-control" type="text" />
                    )}
                  </div>
                ))
              ) : (
                <p>No questions loaded. Please try again.</p>
              )}
              <button type="submit" className="btn btn-primary w-100" disabled={questions.length === 0}>Submit Request</button>
              <button type="button" className="btn btn-secondary w-100 mt-2" onClick={() => setStep(1)}>Back</button>
            </form>
          )}
          <div className="text-center mb-4">
            <button className="btn btn-link" onClick={toggleProviderMode}>Register as a Provider</button>
          </div>
        </div>
      </div>
      <div className="ad-placeholder mb-4 text-center" style={{ backgroundColor: '#e2e8f0', height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '5px' }}>
        <p className="text-muted">Google Ad Banner (728x90)</p>
      </div>
      <h2 className="text-center mb-3">Recent Service Requests</h2>
      <div className="row mb-4 gx-2">
        {recentRequests.slice(0, 8).map(req => (
          <div className="col-12 col-md-3 mb-2" key={req._id}>
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title">{req.serviceCategory} in {req.city}</h5>
                <p className="card-text">{req.scope}</p>
                <small className="text-muted">Posted: {new Date(req.createdAt).toLocaleDateString()}</small>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="row">
        <div className="col-12 text-center mb-4">
          <Link to="/all-requests" className="btn btn-link">Show All Past Requests</Link>
          <Link to="/admin/review" className="btn btn-link">Admin Review</Link>
        </div>
      </div>
      <div className="row">
        <div className="col-md-4 mx-auto">
          <div className="ad-placeholder mb-4" style={{ backgroundColor: '#e2e8f0', height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '5px' }}>
            <p className="text-muted">Google Ad (250x250)</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminReview() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [pendingProviders, setPendingProviders] = useState([]);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      fetchPendingProviders();
    }
  }, [isAuthenticated]);

  const fetchPendingProviders = async () => {
    try {
      const res = await axios.get('/api/admin/pending-providers');
      setPendingProviders(res.data);
    } catch (error) {
      console.error('Error fetching pending providers:', error);
      setMessage('Error fetching pending providers');
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    const ADMIN_PASSWORD = 'fixxzap2025';
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setMessage('');
    } else {
      setMessage('Invalid password');
    }
  };

  const handleReview = async (providerId, action) => {
    try {
      const res = await axios.post('/api/admin/review-provider', {
        providerId,
        action,
        password: 'fixxzap2025'
      });
      console.log('Review response:', res.data);
      setPendingProviders(pendingProviders.filter(p => p._id !== providerId));
      setMessage(`Provider ${action}d successfully`);
    } catch (error) {
      console.error('Error reviewing provider:', error.response ? error.response.data : error.message);
      setMessage(error.response?.data?.message || 'Error reviewing provider');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="container-fluid mt-5">
        <Helmet>
          <title>Fixxzap - Admin Login</title>
        </Helmet>
        <div className="card p-4 shadow-sm mx-auto" style={{ maxWidth: '400px' }}>
          <h2 className="text-center mb-4">Admin Login</h2>
          <form onSubmit={handlePasswordSubmit}>
            <div className="mb-3">
              <label className="form-label">Password</label>
              <input
                type="password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
              />
            </div>
            {message && <p className="text-danger text-center">{message}</p>}
            <button type="submit" className="btn btn-primary w-100">Login</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-5">
      <Helmet>
        <title>Fixxzap - Admin Review</title>
      </Helmet>
      <div className="row">
        <div className="col-12 text-center mb-4">
          <h2 style={{ fontSize: '1.5rem' }}>Admin Review</h2>
          <Link to="/admin/add-provider" className="btn btn-link">Add Provider</Link>
        </div>
      </div>
      <div className="row">
        <div className="col-md-8 mx-auto">
          <h3 className="mb-3">Pending Providers</h3>
          {pendingProviders.length > 0 ? (
            pendingProviders.map(provider => (
              <div key={provider._id} className="card mb-3 p-3 shadow-sm">
                <h5>{provider.name}</h5>
                <p>Email: {provider.email}</p>
                <p>Services: {provider.services.join(', ')}</p>
                <p>Cities: {provider.cities.join(', ')}</p>
                <div className="d-flex justify-content-end">
                  <button
                    className="btn btn-success me-2"
                    onClick={() => handleReview(provider._id, 'approve')}
                  >
                    Approve
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleReview(provider._id, 'reject')}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center">No pending providers to review.</p>
          )}
          {message && <p className={`mt-3 text-center ${message.includes('Error') ? 'text-danger' : 'text-success'}`}>{message}</p>}
        </div>
      </div>
    </div>
  );
}

function Root() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainApp />} />
        <Route path="/all-requests" element={<AllRequests />} />
        <Route path="/admin/review" element={<AdminReview />} />
        <Route path="/admin/add-provider" element={<AddProvider />} />
      </Routes>
    </Router>
  );
}

export default Root;
